/**
 * @file
 * Contains the DropDown component used for filtering EBAB data.
 */
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
    styled,
    Autocomplete,
    TextField,
    Chip,
    Popper,
    ListItem
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import {
    screamingPeerCategoryToKebab,
    screamingPeerCategoryToTitle
} from '../../functions/dataFormatting';
import Box from '@mui/material/Box';

const StyledCloseIcon = styled(CloseIcon)(({ theme }) => ({
    color: theme.palette.common.white + '!important',
    fontSize: 20
}));

const StyledAutocomplete = styled(Autocomplete)(({ theme }) => ({
    padding: 0,
    '& .MuiInputBase-input': {
        cursor: 'pointer'
    },
    '& .MuiOutlinedInput-root': {
        borderRadius: 50,
        '&:hover .MuiOutlinedInput-notchedOutline': {
            //hover border colour
            borderColor: theme.palette.filters.selected
        },
        '&:focus-within .MuiOutlinedInput-notchedOutline': {
            //selected border colour
            borderColor: theme.palette.filters.selected
        }
    },
    '& .MuiFormControl-root': {
        borderRadius: 50,
        '&:focus-within': {
            //selected background colour
            background: theme.palette.filters.selected
        },
        '&:hover': {
            //hover background colour
            backgroundColor: theme.palette.filters.selected
        }
    },
    //non-selected border colour
    '& .MuiOutlinedInput-notchedOutline': {
        borderColor: theme.palette.filters.unselected
    }
}));

const StyledListbox = styled('ul')(({ theme }) => ({
    //dropdown options background colour
    backgroundColor: theme.palette.filters.selected
}));

const StyledPopper = styled(Popper)(() => ({
    //gap between dropdown and options list
    paddingTop: 4
}));

/**
 * Transparent Box element used to temporarily block hover events on RankingTable rows
 * while filtering takes place. Is shown for a split second after dropdown closes.
 * @component
 * @returns {React.ReactElement}
 */
const TransparentHoverBlocker = () => (
    <Box
        sx={{
            position: 'absolute',
            height: 300,
            width: '100%',
            top: 0,
            left: 0,
            backgroundColor: 'transparent',
            zIndex: 100
        }}
    />
);

/**
 * The `Dropdown` component renders a dropdown menu with provided multi-select options.
 * It uses the MUI `Autocomplete` component, with the text input disabled, and custom styles for background, border, options, and popper.
 * Each dropdown option can be selected by the user, which will then show a deleteable chip with the selection.
 *
 * @component
 * @param {Object} props - The component's props.
 * @param {string} id - The id for the dropdown.
 * @param {string} props.label - The label for the dropdown.
 * @param {Array} props.values - The currently selected values of the dropdown.
 * @param {Function} props.onChange - A function that gets called when the dropdown value changes.
 * @param {Array} props.options - An array of strings containing the available dropdown options
 * @returns {React.ReactElement} The rendered Dropdown component.
 */

const Dropdown = ({
    id,
    label,
    onChange,
    values,
    options,
    dataAnalyticsId
}) => {
    const [showHoverBlocker, setShowHoverBlocker] = useState(false);

    return (
        <>
            <StyledAutocomplete
                data-analyticsid={dataAnalyticsId}
                sx={{ padding: 0 }}
                multiple
                fullWidth
                id={id}
                options={options}
                value={values}
                ListboxComponent={StyledListbox}
                PopperComponent={StyledPopper}
                onChange={onChange}
                filterSelectedOptions
                disableClearable
                onOpen={() => setShowHoverBlocker(true)}
                onClose={() =>
                    setTimeout(() => {
                        setShowHoverBlocker(false);
                    }, 80)
                }
                renderInput={({ inputProps, ...params }) => (
                    <TextField
                        {...params}
                        label={label}
                        inputProps={{
                            ...inputProps,
                            readOnly: true,
                            style: {
                                minWidth: 0,
                                paddingRight: 0,
                                paddingLeft: 0
                            }
                        }}
                        data-analyticsid={screamingPeerCategoryToKebab(label)}
                    />
                )}
                renderTags={(tagValue, getTagProps) =>
                    tagValue.map((option, index) => (
                        <Chip
                            {...getTagProps({ index })}
                            key={option}
                            data-analyticsid={`${screamingPeerCategoryToKebab(option)}-chip`}
                            size={'small'}
                            label={screamingPeerCategoryToTitle(option)}
                            deleteIcon={<StyledCloseIcon />}
                            onClick={getTagProps({ index }).onDelete}
                        />
                    ))
                }
                renderOption={(props, option, state) => (
                    <ListItem
                        {...props}
                        key={option}
                        data-analyticsid={screamingPeerCategoryToKebab(option)}
                    >
                        <Box>{screamingPeerCategoryToTitle(option)}</Box>
                    </ListItem>
                )}
            />
            {showHoverBlocker && <TransparentHoverBlocker />}
        </>
    );
};

Dropdown.propTypes = {
    id: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    onChange: PropTypes.func.isRequired,
    options: PropTypes.array.isRequired,
    values: PropTypes.array.isRequired,
    dataAnalyticsId: PropTypes.string
};

export default Dropdown;
